import React, { useState, useEffect } from 'react';
import { FileText, Plus, Trash2, X, User, CreditCard } from 'lucide-react';
import { apiCall, getVATBreakdown } from './invoiceApi';

/**
 * Invoice Modal
 * Modal for creating and editing invoices and credit notes
 * Uses TTC (tax-inclusive price) as the source of truth to avoid rounding errors
 */
const InvoiceModal = ({ invoice, templates, vatRates, language, onClose, onSave, defaultTemplateId, isCreditNote = false }) => {
  // Initialize items - convert existing invoice items to use TTC as source of truth
  const initializeItems = () => {
    if (invoice?.items?.length) {
      return invoice.items.map(item => ({
        ...item,
        // Use stored unit_price_ttc if available, otherwise calculate from HTVA
        unit_price_ttc: item.unit_price_ttc || (item.unit_price * (1 + (item.vat_rate || 21) / 100)),
      }));
    }
    return [{ description: '', quantity: 1, unit_price_ttc: 0, vat_rate: 21, product_id: '', size: '', color: '' }];
  };

  // Use invoice template, or default template from settings, or 'moderne' as last fallback
  const getInitialTemplateId = () => {
    if (invoice?.template_id) return invoice.template_id;
    if (defaultTemplateId) return defaultTemplateId;
    return 'moderne';
  };

  // Check if this is a credit note (from prop or from existing invoice)
  const isCredit = isCreditNote || invoice?.is_credit_note;

  const [formData, setFormData] = useState({
    customer: invoice?.customer || { name: '', email: '', address: '', postal_code: '', city: '', country: 'Belgique', vat_number: '' },
    items: initializeItems(),
    template_id: getInitialTemplateId(),
    language: invoice?.language || 'fr',
    notes: invoice?.notes || '',
    due_days: 30,
    is_credit_note: isCredit,
  });
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [showNewClient, setShowNewClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [existingClientMatch, setExistingClientMatch] = useState(null);

  // Load clients and products on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, productsRes] = await Promise.all([
          apiCall('/api/clients'),
          apiCall(`/api/products-for-invoice?language=${language}`),
        ]);
        setClients(clientsRes.clients || []);
        setProducts(productsRes.products || []);
      } catch (error) {
        console.error('Load error:', error);
      }
    };
    loadData();
  }, [language]);

  // Check for existing client when email changes
  useEffect(() => {
    const email = formData.customer.email?.toLowerCase().trim();
    if (email && email.includes('@')) {
      const match = clients.find(c => c.email?.toLowerCase() === email);
      setExistingClientMatch(match || null);
    } else {
      setExistingClientMatch(null);
    }
  }, [formData.customer.email, clients]);

  // Filter clients based on search
  const filteredClients = clients.filter(c => 
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.email.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Handle client selection
  const handleClientSelect = (clientId) => {
    if (clientId === 'new') {
      setShowNewClient(true);
      setSelectedClientId('');
      setFormData(prev => ({
        ...prev,
        customer: { name: '', email: '', address: '', postal_code: '', city: '', country: 'Belgique', vat_number: '' }
      }));
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setSelectedClientId(clientId);
        setShowNewClient(false);
        setFormData(prev => ({
          ...prev,
          customer: {
            name: client.name,
            email: client.email,
            address: client.address,
            postal_code: client.postal_code,
            city: client.city,
            country: client.country,
            vat_number: client.vat_number || '',
          }
        }));
      }
    }
  };

  // Handle product selection for an item - USE TTC PRICE as source of truth
  const handleProductSelect = (index, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map((item, i) => i === index ? {
          ...item,
          product_id: productId,
          description: product.name,
          unit_price_ttc: product.price_ttc,
          size: product.sizes[0] || '',
          color: product.colors[0] || '',
        } : item),
      }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price_ttc: 0, vat_rate: 21, product_id: '', size: '', color: '' }],
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item),
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
    }
  };

  // Calculate totals - TTC is the source of truth
  const calculateTotalTTC = () => {
    return formData.items.reduce((total, item) => {
      const lineTTC = (item.unit_price_ttc || 0) * (item.quantity || 1);
      return total + Math.round(lineTTC * 100) / 100;
    }, 0);
  };

  const calculateSubtotalHTVA = () => {
    return formData.items.reduce((total, item) => {
      const lineTTC = (item.unit_price_ttc || 0) * (item.quantity || 1);
      const { htva } = getVATBreakdown(lineTTC, item.vat_rate || 21);
      return total + htva;
    }, 0);
  };

  const calculateTotalVAT = () => {
    return formData.items.reduce((total, item) => {
      const lineTTC = (item.unit_price_ttc || 0) * (item.quantity || 1);
      const { tva } = getVATBreakdown(lineTTC, item.vat_rate || 21);
      return total + tva;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Prepare items with unit_price_ttc as source of truth
      const preparedItems = formData.items.map(item => {
        const lineTTC = (item.unit_price_ttc || 0) * (item.quantity || 1);
        const { htva } = getVATBreakdown(item.unit_price_ttc || 0, item.vat_rate || 21);
        return {
          description: item.description,
          quantity: item.quantity,
          unit_price: htva,
          unit_price_ttc: item.unit_price_ttc,
          vat_rate: item.vat_rate || 21,
          total_excl: Math.round(htva * item.quantity * 100) / 100,
          total_ttc: Math.round(lineTTC * 100) / 100,
          size: item.size || '',
          color: item.color || '',
        };
      });

      const dataToSend = { ...formData, items: preparedItems };
      const endpoint = invoice ? `/api/invoicing/invoices/${invoice.id}` : '/api/invoicing/invoices';
      await apiCall(endpoint, { method: invoice ? 'PUT' : 'POST', body: JSON.stringify(dataToSend) });
      onSave();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Determine title based on document type
  const getTitle = () => {
    if (language === 'fr') {
      if (invoice) return isCredit ? 'Modifier la note de crédit' : 'Modifier la facture';
      return isCredit ? 'Nouvelle note de crédit' : 'Nouvelle facture';
    } else {
      if (invoice) return isCredit ? 'Creditnota bewerken' : 'Factuur bewerken';
      return isCredit ? 'Nieuwe creditnota' : 'Nieuwe factuur';
    }
  };

  const t = language === 'fr' ? {
    title: getTitle(),
    customer: 'Client',
    selectClient: 'Sélectionner un client',
    newClient: 'Nouveau client',
    searchClient: 'Rechercher un client...',
    name: 'Nom / Société',
    email: 'Email',
    address: 'Adresse',
    postalCode: 'Code postal',
    city: 'Ville',
    vatNumber: 'N° TVA',
    items: isCredit ? 'Lignes de crédit' : 'Lignes de facture',
    selectProduct: 'Sélectionner un produit',
    manualEntry: 'Saisie manuelle',
    description: 'Description',
    quantity: 'Qté',
    unitPrice: 'Prix HTVA',
    size: 'Taille',
    color: 'Couleur',
    vatRate: 'TVA',
    addItem: 'Ajouter ligne',
    template: 'Modèle',
    invoiceLanguage: 'Langue',
    notes: 'Notes',
    dueDays: 'Échéance (j)',
    subtotalHTVA: 'Sous-total HTVA',
    vat: 'TVA',
    totalTTC: 'Total TTC',
    save: 'Enregistrer',
    cancel: 'Annuler',
  } : {
    title: getTitle(),
    customer: 'Klant',
    selectClient: 'Selecteer een klant',
    newClient: 'Nieuwe klant',
    searchClient: 'Zoek een klant...',
    name: 'Naam / Bedrijf',
    email: 'Email',
    address: 'Adres',
    postalCode: 'Postcode',
    city: 'Stad',
    vatNumber: 'BTW-nummer',
    items: isCredit ? 'Creditnota regels' : 'Factuurregels',
    selectProduct: 'Selecteer een product',
    manualEntry: 'Handmatige invoer',
    description: 'Omschrijving',
    quantity: 'Aantal',
    unitPrice: 'Prijs excl. BTW',
    size: 'Maat',
    color: 'Kleur',
    vatRate: 'BTW',
    addItem: 'Regel toevoegen',
    template: 'Sjabloon',
    invoiceLanguage: 'Taal',
    notes: 'Opmerkingen',
    dueDays: 'Vervaltermijn (d)',
    subtotalHTVA: 'Subtotaal excl. BTW',
    vat: 'BTW',
    totalTTC: 'Totaal incl. BTW',
    save: 'Opslaan',
    cancel: 'Annuleren',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className={`p-6 border-b flex justify-between items-center sticky top-0 z-10 ${isCredit ? 'bg-purple-50' : 'bg-white'}`}>
          <h2 className={`text-xl font-bold flex items-center gap-2 ${isCredit ? 'text-purple-800' : ''}`}>
            {isCredit ? <CreditCard className="w-6 h-6 text-purple-600" /> : <FileText className="w-6 h-6 text-amber-600" />}
            {t.title}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-amber-600" />
              {t.customer}
            </h3>
            
            {/* Client Selection */}
            <div className="mb-4">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder={t.searchClient}
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleClientSelect('new')}
                  className={`px-3 py-2 rounded-lg text-sm ${showNewClient ? 'bg-amber-600 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  {t.newClient}
                </button>
              </div>
              
              {/* Client dropdown */}
              {filteredClients.length > 0 && clientSearch && (
                <div className="bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto mb-4">
                  {filteredClients.slice(0, 5).map(client => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => {
                        handleClientSelect(client.id);
                        setClientSearch('');
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-amber-50 ${selectedClientId === client.id ? 'bg-amber-100' : ''}`}
                    >
                      <div className="font-medium">{client.name}</div>
                      <div className="text-xs text-gray-500">{client.email} • {client.city}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Customer form */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <input type="text" placeholder={t.name} value={formData.customer.name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, name: e.target.value } }))}
                className="col-span-2 px-3 py-2 border rounded-lg text-sm" required />
              <input type="text" placeholder={t.vatNumber} value={formData.customer.vat_number || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, vat_number: e.target.value } }))}
                className="px-3 py-2 border rounded-lg text-sm" />
              
              {/* Email with existing client detection */}
              <div className="relative">
                <input type="email" placeholder={t.email} value={formData.customer.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, email: e.target.value } }))}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${existingClientMatch ? 'border-green-500 bg-green-50' : ''}`} />
                {existingClientMatch && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-green-100 border border-green-300 rounded-lg p-2 text-xs z-10 shadow-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-green-800 font-medium">
                        ✓ {language === 'fr' ? 'Client existant' : 'Bestaande klant'}: {existingClientMatch.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            customer: {
                              name: existingClientMatch.name || prev.customer.name,
                              email: existingClientMatch.email || prev.customer.email,
                              address: existingClientMatch.address || '',
                              postal_code: existingClientMatch.postal_code || '',
                              city: existingClientMatch.city || '',
                              country: existingClientMatch.country || 'Belgique',
                              vat_number: existingClientMatch.vat_number || '',
                              phone: existingClientMatch.phone || '',
                            }
                          }));
                          setSelectedClientId(existingClientMatch.id);
                          setExistingClientMatch(null);
                        }}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                      >
                        {language === 'fr' ? 'Utiliser' : 'Gebruiken'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <input type="text" placeholder={t.address} value={formData.customer.address}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, address: e.target.value } }))}
                className="col-span-2 px-3 py-2 border rounded-lg text-sm" />
              <input type="text" placeholder={t.postalCode} value={formData.customer.postal_code}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, postal_code: e.target.value } }))}
                className="px-3 py-2 border rounded-lg text-sm" />
              <input type="text" placeholder={t.city} value={formData.customer.city}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, city: e.target.value } }))}
                className="px-3 py-2 border rounded-lg text-sm" />
              <input type="tel" placeholder={language === 'fr' ? 'Téléphone' : 'Telefoon'} value={formData.customer.phone || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, customer: { ...prev.customer, phone: e.target.value } }))}
                className="px-3 py-2 border rounded-lg text-sm" />
            </div>
          </div>

          {/* Items Section */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                {t.items}
              </h3>
              <button type="button" onClick={addItem} className="text-amber-700 text-sm flex items-center gap-1 hover:text-amber-800">
                <Plus className="w-4 h-4" />{t.addItem}
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.items.map((item, index) => {
                const selectedProduct = products.find(p => p.id === item.product_id);
                return (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex gap-2 items-start flex-wrap">
                      {/* Product Selection */}
                      <div className="w-full md:w-auto md:flex-1">
                        <select
                          value={item.product_id || ''}
                          onChange={(e) => handleProductSelect(index, e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                        >
                          <option value="">{t.selectProduct}</option>
                          <option value="manual">{t.manualEntry}</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} - €{p.price_htva.toFixed(2)} HTVA
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Size & Color (if product selected) */}
                      {selectedProduct && (
                        <>
                          <select
                            value={item.size || ''}
                            onChange={(e) => updateItem(index, 'size', e.target.value)}
                            className="w-20 px-2 py-2 border rounded-lg text-sm bg-white"
                          >
                            {selectedProduct.sizes.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <select
                            value={item.color || ''}
                            onChange={(e) => updateItem(index, 'color', e.target.value)}
                            className="w-24 px-2 py-2 border rounded-lg text-sm bg-white"
                          >
                            {selectedProduct.colors.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </>
                      )}
                      
                      {/* Manual description if not from product */}
                      {!item.product_id || item.product_id === 'manual' ? (
                        <input
                          type="text"
                          placeholder={t.description}
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                          required
                        />
                      ) : null}
                      
                      {/* Quantity */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">{t.quantity}</span>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-16 px-2 py-2 border rounded-lg text-sm text-center"
                          min="1"
                        />
                      </div>
                      
                      {/* Unit Price TTC */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500">Prix TTC</span>
                        <input
                          type="number"
                          value={item.unit_price_ttc || 0}
                          step="0.01"
                          onChange={(e) => updateItem(index, 'unit_price_ttc', parseFloat(e.target.value) || 0)}
                          className="w-24 px-2 py-2 border rounded-lg text-sm text-right"
                        />
                      </div>
                      
                      {/* VAT Rate */}
                      <select
                        value={item.vat_rate}
                        onChange={(e) => updateItem(index, 'vat_rate', parseFloat(e.target.value))}
                        className="w-20 px-2 py-2 border rounded-lg text-sm bg-white"
                      >
                        {vatRates.map((rate) => (
                          <option key={rate.id} value={rate.rate}>{rate.rate}%</option>
                        ))}
                      </select>
                      
                      {/* Line Total */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-amber-700">
                          €{((item.unit_price_ttc || 0) * (item.quantity || 1)).toFixed(2)} TTC
                        </span>
                        {formData.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t.template}</label>
              <select value={formData.template_id}
                onChange={(e) => setFormData(prev => ({ ...prev, template_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg">
                {templates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>{language === 'fr' ? tmpl.name : tmpl.name_nl}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.invoiceLanguage}</label>
              <select value={formData.language}
                onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg">
                <option value="fr">Français</option>
                <option value="nl">Nederlands</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t.dueDays}</label>
              <input type="number" value={formData.due_days}
                onChange={(e) => setFormData(prev => ({ ...prev, due_days: parseInt(e.target.value) || 30 }))}
                className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">{t.notes}</label>
            <textarea value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2} className="w-full px-3 py-2 border rounded-lg" />
          </div>

          {/* Totals & Actions */}
          <div className="flex justify-between items-center pt-4 border-t bg-amber-50 -mx-6 -mb-6 px-6 py-4 rounded-b-xl">
            <div className="space-y-1">
              <div className="text-sm text-gray-600">
                {t.subtotalHTVA}: <span className="font-medium">€{calculateSubtotalHTVA().toFixed(2)}</span>
              </div>
              <div className="text-sm text-gray-600">
                {t.vat}: <span className="font-medium">€{calculateTotalVAT().toFixed(2)}</span>
              </div>
              <div className="text-lg font-bold text-amber-800">
                {t.totalTTC}: €{calculateTotalTTC().toFixed(2)}
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">{t.cancel}</button>
              <button type="submit" disabled={saving}
                className="px-6 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg font-medium disabled:opacity-50">
                {saving ? '...' : t.save}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceModal;
